import type { ReactNode } from "react";
import { ChevronDown, X } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";

/** Kleine Kachel rechts neben dem Daily Digest, wie die Konto-Kacheln im Brokers-Tab:
 *  die ganze Kachel ist der Knopf, das Panel klappt in voller Breite UNTER der Zeile
 *  auf (der Digest behaelt so seine Breite). */
export function AiTile({
  open,
  onToggle,
  title,
  toggleId,
  children,
}: {
  open: boolean;
  onToggle: () => void;
  title: string;
  toggleId: string;
  /** Kurzzeilen unter dem Titel. */
  children?: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      data-ai-toggle={toggleId}
      className={cn(
        "flex w-full min-w-0 flex-col gap-1 rounded-xl border bg-card p-3 text-left shadow-sm transition-colors",
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        open ? "border-primary ring-1 ring-primary/30" : "border-border hover:border-primary/50",
      )}
    >
      <span className="flex items-center justify-between gap-2">
        <span className="truncate text-sm font-semibold text-primary">{title}</span>
        <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-md bg-muted/70">
          <ChevronDown className={cn("h-3 w-3 text-muted-foreground transition-transform", open && "rotate-180")} />
        </span>
      </span>
      {children}
    </button>
  );
}

/** Aufgeklapptes Panel unter der Digest-Zeile, mit X zum Zuklappen wie im Brokers-Tab. */
export function AiPanel({
  title,
  description,
  onClose,
  actions,
  children,
  panelId,
}: {
  title: string;
  description?: ReactNode;
  onClose: () => void;
  actions?: ReactNode;
  children: ReactNode;
  panelId: string;
}) {
  return (
    <Card className="h-full" data-ai-panel={panelId}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 space-y-1.5">
            <CardTitle>{title}</CardTitle>
            {description && <CardDescription>{description}</CardDescription>}
          </div>
          <div className="flex shrink-0 items-center gap-3">
            {actions}
            <button
              type="button"
              onClick={onClose}
              aria-label={`${title} zuklappen`}
              className="rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}
